<template>
  <div class="preview-actions">
    <DpplDarkTooltip
      v-if="isCreateButtonDisabled"
      tooltip-placement="bottom"
      light-mode
    >
      <template #activator="{ onMouseenter, onMouseleave }">
        <span
          class="create-btn-tooltip-wrapper"
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
        >
          <v-btn
            class="preview-action-btn create-btn create-btn-disabled"
            variant="outlined"
            size="small"
            icon
            disabled
          >
            <v-icon icon="$plus" />
          </v-btn>
        </span>
      </template>
      <template #tooltip-content>
        <p class="text-body-2 text-textDark">
          {{ createButtonDisabledTooltip }}
        </p>
      </template>
    </DpplDarkTooltip>
    <v-btn
      v-else
      class="preview-action-btn create-btn"
      variant="outlined"
      color="brandPrimary"
      size="small"
      icon
      @click="emit('create')"
    >
      <v-icon icon="$plus" />
    </v-btn>
    <DpplDarkTooltip
      tooltip-placement="bottom"
      light-mode
    >
      <template #activator="{ onMouseenter, onMouseleave }">
        <span
          class="download-btn-tooltip-wrapper"
          @mouseenter="onMouseenter"
          @mouseleave="onMouseleave"
        >
          <v-btn
            class="preview-action-btn download-btn"
            :class="{ 'download-btn-disabled': !canDownloadAllPDFs }"
            variant="outlined"
            size="small"
            icon
            :disabled="!canDownloadAllPDFs"
            @click="downloadPDFs"
          >
            <v-icon icon="$tray-arrow-down" />
          </v-btn>
        </span>
      </template>
      <template #tooltip-content>
        <p class="text-body-2 text-textDark">
          {{ downloadAllTooltipText }}
        </p>
      </template>
    </DpplDarkTooltip>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import DpplDarkTooltip from '@/components/datapeople/DpplDarkTooltip.vue';
import {
  getEmployeeAcknowledgementDownloadPdfUrl,
  getEmployeeAcknowledgementDownloadZipUrl,
} from '@/api/jobProfile';
import { clickToDownload } from '@/helpers/fileDownload';
import { useAppSnackbar } from '@/stores/app/snackbar';

const MAX_SIGNED_FOR_DOWNLOAD_ALL = 15;

const props = defineProps({
  previewData: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['create']);

const { addSuccessSnackbar, addErrorSnackbar } = useAppSnackbar();

const currentAcknowledgement = computed(() => {
  if (!props.previewData?.acknowledgements) return null;

  return props.previewData.acknowledgements.find((ack) => ack.is_current);
});

const hasSignedAcknowledgements = computed(() => (
  (props.previewData?.acknowledgements || []).some((ack) => ack.status === 'signed')
));

const signedAcknowledgementsCount = computed(() => (
  (props.previewData?.acknowledgements || []).filter((ack) => ack.status === 'signed').length
));

const canDownloadAllPDFs = computed(() => (
  hasSignedAcknowledgements.value && signedAcknowledgementsCount.value <= MAX_SIGNED_FOR_DOWNLOAD_ALL
));

const downloadAllTooltipText = computed(() => {
  if (!hasSignedAcknowledgements.value) {
    return 'No signed acknowledgement PDFs available to download.';
  }

  if (signedAcknowledgementsCount.value > MAX_SIGNED_FOR_DOWNLOAD_ALL) {
    return 'Too many signed PDFs to download at once (max 15). '
      + 'Download them one by one from the list below.';
  }

  return 'Download All PDFs';
});

const isPlusDisabled = computed(() => currentAcknowledgement.value?.status === 'pending');

const isEmployeeActive = computed(() => Boolean(props.previewData?.is_active));
const hasEmployeeEmail = computed(() => Boolean(props.previewData?.email));

const hasJobProfile = computed(() => Boolean(props.previewData?.job_profile_ulid
  || props.previewData?.job_profile_title));

const isCreateButtonDisabled = computed(() => (
  isPlusDisabled.value || !isEmployeeActive.value || !hasEmployeeEmail.value || !hasJobProfile.value
));

const createButtonDisabledTooltip = computed(() => {
  if (isPlusDisabled.value) {
    return 'Current pending acknowledgement must be resolved prior to creating a new one.';
  }

  if (!isEmployeeActive.value) {
    return 'New acknowledgements cannot be created for inactive employees.';
  }

  if (!hasEmployeeEmail.value) {
    return 'Please assign an e-mail address to this employee in your HRIS in order to create an acknowledgement.';
  }

  if (!hasJobProfile.value) {
    return 'Please assign a job profile to this employee in your HRIS in order to create an acknowledgement.';
  }

  return '';
});

const downloadPDFs = () => {
  const tooManyToDownloadSnackbarMessage = `Too many to download at once. 
    Please download them one by one from the list below.`;

  const acknowledgements = props.previewData?.acknowledgements || [];
  const signedAcknowledgements = acknowledgements.filter((ack) => ack.status === 'signed');

  if (!signedAcknowledgements.length) return;

  if (signedAcknowledgements.length > MAX_SIGNED_FOR_DOWNLOAD_ALL) {
    addErrorSnackbar({ text: tooManyToDownloadSnackbarMessage });

    return;
  }

  const count = signedAcknowledgements.length;

  try {
    if (count === 1) {
      const pdfUrl = getEmployeeAcknowledgementDownloadPdfUrl(signedAcknowledgements[0].ulid);

      clickToDownload(pdfUrl);

      addSuccessSnackbar({ text: 'Your PDF is downloading.' });
    } else {
      const ulids = signedAcknowledgements.map((ack) => ack.ulid);
      const zipUrl = getEmployeeAcknowledgementDownloadZipUrl(ulids);

      clickToDownload(zipUrl);

      addSuccessSnackbar({
        text: `Your ZIP file with ${count} PDFs is downloading.`,
      });
    }
  } catch (error) {
    addErrorSnackbar({
      text: error?.message || 'Failed to start PDF download.',
    });
  }
};
</script>

<style scoped lang="less">
.preview-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.preview-action-btn {
  min-width: 36px;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: @bg-default;
}

.download-btn {
  border: 1px solid @stroke-default;
  color: @text-dark;
  background-color: @bg-default;
}

.download-btn-disabled {
  background-color: @grey-200 !important;
}

.create-btn-disabled {
  background-color: @grey-200 !important;
  border: 1px solid @stroke-default;
  color: @text-dark;
}

.create-btn-tooltip-wrapper {
  display: inline-block;
}

.download-btn-tooltip-wrapper {
  display: inline-block;
}
</style>
